const resources = {
  en: {
    forecastSummaryPage: {
      title: 'Forecast summary',
      description: 'Project future balance changes from the selected anchor date and connected recurring flows.',
      meta: {
        anchorDate: 'Anchor date: {{date}}',
      },
      filters: {
        anchorDay: 'Anchor day',
        monthsAhead: 'Months ahead',
      },
      status: {
        risk: 'Balance may go negative in this window',
      },
      executionMode: {
        ledgerFallbackShadow: 'Admin-only fallback forecast',
      },
      metrics: {
        anchorDate: 'Anchor date',
        startingBalance: 'Starting balance',
        startingBalanceSource: {
          live: 'Source: live spendable balances from selected accounts.',
          ledger: 'Source: rebuilt from the native transaction ledger because live balances were unavailable.',
          empty: 'Source: no tracked accounts are selected.',
        },
        closingBalance: 'Closing balance',
        expectedIncome: 'Expected income',
        expectedExpense: 'Expected expenses',
      },
      blocked: {
        title: 'Forecast blocked for this account mix',
        description: 'The selected live-balance accounts span multiple currencies, so this forecast stays in protected mode.',
        summary: 'Keep the protected guard in place and review the live-balance source setup before relying on forecast balances.',
        stats: {
          selectedAccounts: '{{count}} selected accounts',
          resolvedAccounts: '{{count}} resolved accounts',
          liveBalanceReady: '{{count}} with live balances',
          missingLiveBalances: '{{count}} missing live balances',
          currencies: 'Currencies: {{currencies}}',
        },
        actions: {
          label: 'Next step',
          none: 'No immediate remediation is required. The protected guard stays in place because the selection is mixed-currency.',
          review_missing_selected_accounts: 'Review the selected accounts list. Some configured live-balance accounts could not be resolved.',
          review_live_balance_source_coverage: 'Review live-balance coverage for the selected accounts before relying on this forecast.',
          consider_native_ledger_fallback: 'A native-ledger fallback preview looks viable, but it remains preview-only for now.',
          unknown: 'Review the selected live-balance account configuration.',
        },
        previewAvailable: 'Native-ledger fallback preview is available for operator inspection.',
        fallbackAction: {
          open: 'Open fallback forecast',
        },
        preview: {
          title: 'Admin shadow preview',
          startingBalance: 'Starting balance: {{amount}}',
          accountsValued: '{{valued}} / {{total}} accounts valued',
          sources: {
            liveBalance: 'Live-balance shadow',
            ledgerFallback: 'Native-ledger fallback',
          },
          completeness: {
            complete: 'Complete coverage',
            partial: 'Partial coverage',
          },
          freshness: {
            fresh: 'Fresh quotes',
            stale: 'Stale quotes',
            degraded: 'Degraded freshness',
          },
        },
        execution: {
          title: 'Fallback forecast preview',
          description: 'Projected closing balance: {{closingBalance}}',
          entriesValued: '{{valued}} / {{total}} forecast entries valued',
          risk: {
            stable: 'Preview stays above zero',
            negative: 'Preview may go negative',
          },
          metrics: {
            startingBalance: 'Preview starting balance: {{amount}}',
            expectedIncome: 'Preview expected income: {{amount}}',
            expectedExpense: 'Preview expected expense: {{amount}}',
          },
        },
      },
      sections: {
        liveAccounts: {
          title: 'Live source accounts',
          description: 'Accounts contributing live balance data to this forecast window.',
        },
        expectedExpenses: {
          title: 'Expected expenses',
          description: 'Projected outgoing flows in the selected forecast window.',
        },
        expectedIncomes: {
          title: 'Expected income',
          description: 'Projected incoming flows in the selected forecast window.',
        },
        timeline: {
          title: 'Balance timeline',
          description: 'Running balance after each projected event in chronological order.',
        },
      },
      timeline: {
        date: 'Date',
        type: 'Type',
        amount: 'Amount',
        balance: 'Balance',
        types: {
          expense: 'Expense',
          income: 'Income',
          summary: 'Month end',
        },
      },
      empty: {
        none: 'None',
      },
      errors: {
        load: 'Failed to load forecast summary',
      },
    },
  },
  uk: {
    forecastSummaryPage: {
      title: 'Підсумок прогнозу',
      description: 'Прогнозуйте майбутні зміни балансу від вибраної опорної дати та підключених регулярних потоків.',
      meta: {
        anchorDate: 'Опорна дата: {{date}}',
      },
      filters: {
        anchorDay: 'Опорний день',
        monthsAhead: 'Місяців уперед',
      },
      status: {
        risk: 'У цьому вікні баланс може піти в мінус',
      },
      executionMode: {
        ledgerFallbackShadow: 'Fallback forecast лише для admin',
      },
      metrics: {
        anchorDate: 'Опорна дата',
        startingBalance: 'Початковий баланс',
        startingBalanceSource: {
          live: 'Джерело: доступні live-баланси вибраних рахунків.',
          ledger: 'Джерело: відновлено з нативного журналу транзакцій, бо live-баланси були недоступні.',
          empty: 'Джерело: не вибрано жодного відстежуваного рахунку.',
        },
        closingBalance: 'Підсумковий баланс',
        expectedIncome: 'Очікувані доходи',
        expectedExpense: 'Очікувані витрати',
      },
      blocked: {
        title: 'Прогноз заблоковано для цього набору рахунків',
        description: 'Вибрані рахунки для live-балансу мають різні валюти, тому прогноз залишається в захищеному режимі.',
        summary: 'Залишайте захисний guard увімкненим і перевірте конфігурацію джерел live-балансу, перш ніж покладатися на цей прогноз.',
        stats: {
          selectedAccounts: '{{count}} вибраних рахунків',
          resolvedAccounts: '{{count}} вирішених рахунків',
          liveBalanceReady: '{{count}} з live-балансами',
          missingLiveBalances: '{{count}} без live-балансів',
          currencies: 'Валюти: {{currencies}}',
        },
        actions: {
          label: 'Наступний крок',
          none: 'Негайна ремедіація не потрібна. Захисний guard залишається ввімкненим, бо вибірка мультивалютна.',
          review_missing_selected_accounts: 'Перевірте список вибраних рахунків. Частину налаштованих джерел live-балансу не вдалося знайти.',
          review_live_balance_source_coverage: 'Перевірте покриття live-балансів для вибраних рахунків, перш ніж покладатися на цей прогноз.',
          consider_native_ledger_fallback: 'Preview нативного ledger fallback виглядає життєздатним, але поки що лишається лише preview.',
          unknown: 'Перевірте конфігурацію вибраних джерел live-балансу.',
        },
        previewAvailable: 'Preview нативного ledger fallback доступний для operator inspection.',
        fallbackAction: {
          open: 'Відкрити fallback forecast',
        },
        preview: {
          title: 'Admin shadow preview',
          startingBalance: 'Початковий баланс: {{amount}}',
          accountsValued: 'Оцінено рахунків: {{valued}} / {{total}}',
          sources: {
            liveBalance: 'Live-balance shadow',
            ledgerFallback: 'Native-ledger fallback',
          },
          completeness: {
            complete: 'Повне покриття',
            partial: 'Часткове покриття',
          },
          freshness: {
            fresh: 'Свіжі котирування',
            stale: 'Застарілі котирування',
            degraded: 'Погіршена свіжість',
          },
        },
        execution: {
          title: 'Preview fallback forecast',
          description: 'Прогнозований closing balance: {{closingBalance}}',
          entriesValued: 'Оцінено forecast entries: {{valued}} / {{total}}',
          risk: {
            stable: 'Preview лишається вище нуля',
            negative: 'Preview може піти в мінус',
          },
          metrics: {
            startingBalance: 'Preview starting balance: {{amount}}',
            expectedIncome: 'Preview expected income: {{amount}}',
            expectedExpense: 'Preview expected expense: {{amount}}',
          },
        },
      },
      sections: {
        liveAccounts: {
          title: 'Живі рахунки-джерела',
          description: 'Рахунки, які додають живі дані балансу до цього прогнозного вікна.',
        },
        expectedExpenses: {
          title: 'Очікувані витрати',
          description: 'Прогнозовані вихідні потоки в межах вибраного прогнозного вікна.',
        },
        expectedIncomes: {
          title: 'Очікувані доходи',
          description: 'Прогнозовані вхідні потоки в межах вибраного прогнозного вікна.',
        },
        timeline: {
          title: 'Хронологія балансу',
          description: 'Поточний баланс після кожної прогнозованої події в хронологічному порядку.',
        },
      },
      timeline: {
        date: 'Дата',
        type: 'Тип',
        amount: 'Сума',
        balance: 'Баланс',
        types: {
          expense: 'Витрата',
          income: 'Доход',
          summary: 'Кінець місяця',
        },
      },
      empty: {
        none: 'Немає',
      },
      errors: {
        load: 'Не вдалося завантажити підсумок прогнозу',
      },
    },
  },
}

export default resources
